import { destroy, stopSubmit } from 'redux-form'
import * as Effects from 'redux-saga/effects'
import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import {
  eventWasCreated,
  IEventsBulkCreated,
  IEventWasCreated,
  IRequestCreateEvent,
} from '../../actions/bookingCalendar/event/requestCreateEvent'
import selectReservationTab from '../../actions/bookingCalendar/reservationModal/selectReservationTab'
import {
  toggleCreateReservationModal,
  toggleReservationModalIsSaving,
} from '../../actions/bookingCalendar/reservationModal/toggleReservationModal'
import { callFetch } from '../../actions/fetch'
import toggleInvoiceLoading from '../../actions/invoices/toggleInvoiceLoading'
import closeModal from '../../actions/Modals/closeModal'
import { requestSynchronization } from '../../actions/synchronize/synchronize'
import { fireToastError } from '../../actions/toasts/fireToast'
import fireToastSuccess from '../../actions/toasts/fireToastSucces'
import { ICreateReservationFormValues } from '../../containers/Form/CreateReservation/CreateReservation'
import { Modals } from '../../containers/Modals/ModalContainer'
import { database } from '../../database'
import i18n from '../../i18n'
import requestCreate from '../../models/Event/createEvent'
import { addOffsetDate } from '../../models/License/mappers/mapResponseToLicense'
import { UpdateLimitType } from '../../models/Limit'
import { EventActions } from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { getInvoiceReservationId } from '../../reducers/invoice/getInvoice'
import fetchActionsName, { ActionNames } from '../../utils/FetchActionsName'
import { LimitResponse, LimitType, loadLimits } from '../userAccount/loadLimits'
import updateLimits from '../userAccount/updateLimits'
import { updateReservation } from './updateEvent'
import { Mixpanel } from '../../utils/Mixpanel'
import { getUserId } from '../../reducers/userAccount/selectors/userAccount'
import { getUserCurrency } from '../../reducers/userObject/selectors/object'
import { findRoomById } from '../../reducers/bookingCalendar/selectors/rooms'
import { differenceInCalendarDays } from 'date-fns'

export function* createReservation(event: ICreateReservationFormValues) {
  try {
    const actionNames = fetchActionsName(ActionNames.CreateEvent)
    const [{ calendarId, service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, requestCreate)),
    ])

    const events = yield call(service, calendarId, event)
    const userId = yield select(getUserId)
    const currency = yield select(getUserCurrency)

    for (const event of events) {
      const room = yield select(state =>
        findRoomById(state, { roomId: event.roomId }),
      )

      logEvent(userId, event, room, currency)

      if ('gtag' in window) {
        ;(window as any).gtag('event', 'conversion', {
          send_to: 'AW-927657230/ZGkcCNn0wJADEI7aq7oD',
        })
      }
      yield put(eventWasCreated(event))
    }
  } catch (e) {
    yield Effects.put(requestSynchronization())
    yield Effects.put(selectReservationTab(0))
    yield Effects.put(toggleInvoiceLoading(false))
    throw Error(e.message)
  }
}

export const getEventGuestPropertyLength = (event, key) => {
  if (event.guest && event.guest[key]) {
    return Number(event.guest[key].length)
  }
  return undefined
}

export const getEventPropertyLength = (event, key) => {
  if (key in event && event[key]) {
    return Number(event[key].length)
  }
  return undefined
}

export const getEventCompanyPropertyLength = (event, key) => {
  if (event.company && key in event.company && event.company[key]) {
    return Number(event.company[key].length)
  }
  return undefined
}

export const getEventAdditionalGuestPropertyLength = (event, key) => {
  if (
    event.roomMates &&
    event.roomMates.length >= 1 &&
    key in event.roomMates[0] &&
    event.roomMates[0][key]
  ) {
    return Number(event.roomMates[0][key].length)
  }
  return undefined
}

export const mapProvisionsMethodToEventString = (provisionMethod: string) => {
  if (provisionMethod === i18n.t('payment_type_credit_card')) {
    return 'Credit Card'
  } else if (provisionMethod === i18n.t('payment_type_bank_transfer')) {
    return 'Bank transfer'
  } else if (provisionMethod === i18n.t('payment_type_cash')) {
    return 'Cash'
  } else {
    return provisionMethod
  }
}

export const mapStageToEventString = (stage: string) => {
  switch (stage) {
    case '1':
      return 'Fully paid'
    case '2':
      return 'Lack of payment'
    case '3':
      return 'Down payment made'
    case '4':
      return 'Unavailable'
    case '5':
      return 'Cancelled'
    default:
      return 'Cancelled'
  }
}

export const mapStageToEventTString = (stage: string, down = '0') => {
  switch (stage) {
    case '1':
      return 'reservation_stage_1'
    case '2':
      return 'reservation_stage_2'
    case '3':
      return down === '0'
        ? 'reservation_stage_3_down_payment'
        : 'reservation_stage_3_advance'
    case '4':
      return 'reservation_stage_4'
    case '5':
      return 'reservation_stage_5'
    default:
      return 'reservation_stage_5'
  }
}

function* createEvent(action: IRequestCreateEvent) {
  yield put(toggleReservationModalIsSaving(true))
  const response = yield* loadLimits(LimitType.Add)
  if (response === LimitResponse.Rejected) {
    yield put(toggleReservationModalIsSaving(false))
    return
  }

  try {
    const reservationId = yield Effects.select(getInvoiceReservationId)

    if (reservationId) {
      yield* updateReservation(reservationId, action.event)
    } else {
      yield* createReservation(action.event)
    }

    yield put(toggleCreateReservationModal(false))
    yield put(destroy(action.formName))
    yield put(toggleReservationModalIsSaving(false))

    yield put(fireToastSuccess(i18n.t('reservation_added')))
    yield put(toggleReservationModalIsSaving(false))
    yield put(closeModal(Modals.ReservationDeleteModal))
    yield* updateLimits(UpdateLimitType.Add)
  } catch (e) {
    if (e.message === '409') {
      yield Effects.put(requestSynchronization())
      yield Effects.put(selectReservationTab(0))
      // yield put(fireToastError(i18n.t('reservation_reserved_date_error')))
    } else {
      console.error(e)
      yield put(fireToastError(i18n.t('error_server_response_2')))
    }
    yield put(stopSubmit(action.formName, e))
    yield put(toggleReservationModalIsSaving(false))
  }
}

function* putEventIntoDb(action: IEventWasCreated) {
  const calendarId = yield select(getCalendarId)

  const event = {
    ...action.event,
    bookingPrice: null,
    calendarId,
    dateToPay: action.event.dateToPay
      ? addOffsetDate(action.event.dateToPay).getTime()
      : undefined,
    dateToPayAfterStart: action.event.dateToPayAfterStart
      ? addOffsetDate(action.event.dateToPayAfterStart).getTime()
      : undefined,
    deposit: null,
    finishDate: addOffsetDate(action.event.finishDate).getTime(),
    localTax: null,
    paymentOnPlace: null,
    paymentOnPlaceDate: action.event.paymentOnPlaceDate
      ? addOffsetDate(action.event.paymentOnPlaceDate).getTime()
      : undefined,
    price: null,
    pricePerDay: null,
    provision: null,
    provisionDate: action.event.provisionDate
      ? addOffsetDate(action.event.provisionDate).getTime()
      : undefined,
    savedColor: action.event.customColor
      ? action.event.customColor.rgb().toString()
      : null,
    startDate: addOffsetDate(action.event.startDate).getTime(),
  }

  yield database.events.add(event as any)
}

function* createBulk(action: IEventsBulkCreated) {
  const calendarId = yield select(getCalendarId)
  const events = [...action.events]

  yield database.events.bulkAdd(
    events.map((e): any => {
      return {
        ...e,
        bookingPrice: null,
        calendarId: calendarId,
        dateToPay: e.dateToPay
          ? addOffsetDate(e.dateToPay).getTime()
          : undefined,
        dateToPayAfterStart: e.dateToPayAfterStart
          ? addOffsetDate(e.dateToPayAfterStart).getTime()
          : undefined,
        deposit: null,
        finishDate: addOffsetDate(e.finishDate).getTime(),
        localTax: null,
        paymentOnPlace: null,
        paymentOnPlaceDate: e.paymentOnPlaceDate
          ? addOffsetDate(e.paymentOnPlaceDate).getTime()
          : undefined,
        price: null,
        pricePerDay: null,
        provision: null,
        provisionDate: e.provisionDate
          ? addOffsetDate(e.provisionDate).getTime()
          : undefined,
        savedColor: e.customColor ? e.customColor.rgb().toString() : null,
        startDate: addOffsetDate(e.startDate).getTime(),
      }
    }),
  )
}

export default function*() {
  yield takeLatest(EventActions.RequestCreateEvent, createEvent)
  yield takeEvery(EventActions.EventWasCreated, putEventIntoDb)
  yield takeEvery(EventActions.EventsBulkCreated, createBulk)
}

export const logEvent = (
  userId,
  event,
  room,
  currency,
  update = false,
  invoice: any = false,
) => {
  Mixpanel.people.increment(
    userId,
    update ? 'Edited Reservations' : 'Reservations Created',
    1,
  )
  Mixpanel.people.increment(
    userId,
    'Reservation Final Price Total Amount',
    Number(event._price),
  )
  Mixpanel.people.increment(
    userId,
    'Reservation Down Payment Total Amount',
    Number(event._provision ? event._provision : 0),
  )
  Mixpanel.people.increment(
    userId,
    'Reservation Deposit Total Amount',
    Number(event._deposit ? event._deposit : 0),
  )
  Mixpanel.people.increment(
    userId,
    'Reservation Local Tax Total Amount',
    Number(event._localTax ? event._localTax : 0),
  )
  Mixpanel.people.increment(
    userId,
    'Reservation Additional Services Total Amount',
    Number(
      event.additionalServices
        ? event.additionalServices.reduce((a, b) => a + Number(b.price), 0)
        : 0,
    ),
  )
  Mixpanel.people.set(userId, {
    Currency: currency,
  })

  Mixpanel.track(
    update ? 'Reservation Form Saved' : 'New Reservation Created',
    {
      'Tabs Form View?': true,
      'Reservation ID': Number(event.id),
      'Reservation Room ID': Number(event.roomId),
      'Reservation Room Name': room.name,
      'Reservation No. of days': Math.abs(
        differenceInCalendarDays(event.arrivalTime, event.departureTime),
      ),
      'Reservation Status': mapStageToEventString(event.stage),
      'Reservation Adults': Number(event.adults),
      'Reservation Children': Number(event.kids),
      'Reservation Children Age': JSON.stringify(event.kidsAge),
      'Reservation Custom Colour?': !!event.customColor,
      'Reservation Price Per Day': event._pricePerDay
        ? Number(event._pricePerDay)
        : undefined,
      Currency: currency,
      'Reservation Final Price': event._price
        ? Number(event._price)
        : undefined,
      'Reservation Down Payment': event._provision
        ? Number(event._provision)
        : undefined,
      'Reservation Down Payment Date': !!event.provisionDate,
      'Reservation Down Payment Type': mapProvisionsMethodToEventString(
        event.provisionMethod,
      ),
      'Reservation Down Payment Time To Pay Days': event.dateToPay
        ? Math.abs(differenceInCalendarDays(event.dateToPay, new Date()))
        : undefined,
      'Reservation Down Payment Time To Pay Date': !!event.dateToPay,
      'Reservation Down Payment Time To Pay Days Reminder': event.dateToPayAfterStart
        ? Math.abs(
            differenceInCalendarDays(event.dateToPayAfterStart, new Date()),
          )
        : undefined,
      'Reservation Down Payment Time To Pay Date Reminder': !!event.dateToPayAfterStart,
      'Reservation Payment On Place': event._provision
        ? Number(event._price) - Number(event._provision)
        : Number(event._price),
      'Reservation Payment On Place Date': !!event.paymentOnPlaceDate,
      'Reservation Payment On Place Type': mapProvisionsMethodToEventString(
        event.paymentOnPlaceMethod,
      ),
      'Reservation Deposit': event._deposit
        ? Number(event._deposit)
        : undefined,
      'Reservation Local Tax': event._localTax
        ? Number(event._localTax)
        : undefined,
      'Reservation Local Tax Included?': !!event.localTaxIncluded,
      'Reservation Additional Services Sum': event.additionalServices
        ? event.additionalServices.reduce((a, b) => a + Number(b.price), 0)
        : 0,
      'Reservation Person Name Length': getEventGuestPropertyLength(
        event,
        'name',
      ),
      'Reservation Person Street Length': getEventGuestPropertyLength(
        event,
        'street',
      ),
      'Reservation Person House Number Length': getEventGuestPropertyLength(
        event,
        'houseNumber',
      ),
      'Reservation Person Apartment Number Length': getEventGuestPropertyLength(
        event,
        'apartmentNumber',
      ),
      'Reservation Person City Length': getEventGuestPropertyLength(
        event,
        'city',
      ),
      'Reservation Person Post Code Length': getEventGuestPropertyLength(
        event,
        'postCode',
      ),
      'Reservation Person Country Length': getEventGuestPropertyLength(
        event,
        'country',
      ),
      'Reservation Person Passport Length': getEventGuestPropertyLength(
        event,
        'passport',
      ),
      'Reservation Person National ID Length': getEventGuestPropertyLength(
        event,
        'idCard',
      ),
      'Reservation Person Phone Length': getEventGuestPropertyLength(
        event,
        'phone',
      ),
      'Reservation Person Email Length': getEventGuestPropertyLength(
        event,
        'email',
      ),
      'Reservation Additional Person Name Length': getEventAdditionalGuestPropertyLength(
        event,
        'client_name',
      ),
      'Reservation Additional Person Street Length': getEventAdditionalGuestPropertyLength(
        event,
        'street',
      ),
      'Reservation Additional Person House Number Length': getEventAdditionalGuestPropertyLength(
        event,
        'street_number',
      ),
      'Reservation Additional Person Apartment Number Length': getEventAdditionalGuestPropertyLength(
        event,
        'local_number',
      ),
      'Reservation Additional Person City Length': getEventAdditionalGuestPropertyLength(
        event,
        'city',
      ),
      'Reservation Additional Person Post Code Length': getEventAdditionalGuestPropertyLength(
        event,
        'post_code',
      ),
      'Reservation Additional Person Country Length': getEventAdditionalGuestPropertyLength(
        event,
        'nationality',
      ),
      'Reservation Additional Person Passport Length': getEventAdditionalGuestPropertyLength(
        event,
        'passport',
      ),
      'Reservation Additional Person National ID Length': getEventAdditionalGuestPropertyLength(
        event,
        'id_card',
      ),
      'Reservation Company Name Length': getEventCompanyPropertyLength(
        event,
        'name',
      ),
      'Reservation Company Street Length': getEventCompanyPropertyLength(
        event,
        'street',
      ),
      'Reservation Company City Length': getEventCompanyPropertyLength(
        event,
        'city',
      ),
      'Reservation Company Post Code Length': getEventCompanyPropertyLength(
        event,
        'postCode',
      ),
      'Reservation Company Country Length': getEventCompanyPropertyLength(
        event,
        'country',
      ),
      'Reservation Company VAT ID Length': getEventCompanyPropertyLength(
        event,
        'vatId',
      ),
      'Reservation Notes Length': getEventPropertyLength(event, 'notes'),
      'Reservation Booking Origin Length': getEventPropertyLength(
        event,
        'provenance',
      ),
      'Reservation Entrance Code Length': getEventPropertyLength(
        event,
        'doorCode',
      ),
      'Reservation Single Beds': event.singleBed
        ? Number(event.singleBed)
        : undefined,
      'Reservation Double Beds': event.doubleBed
        ? Number(event.doubleBed)
        : undefined,
      'Reservation Invoice Issued': !!event.invoiceIssued || !!invoice,
    },
  )

  // additionalServices: Array(1)
  // 0:
  // first_tax: "23.00"
  // id: "17486"
  // id_reservation: "568910"
  // price: "321.00"
  // second_tax: ""
  // service_name: "opłata za wynajem quadów"
  //   [[Prototype]]: Object
  // length: 1
  //   [[Prototype]]: Array(0)
  // adults: 1
  // airbnbId: null
  // arrivalTime: Tue Dec 21 2021 16:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // bookingId: "0"
  // bookingPrice: (...)
  // company:
  //   city: "Świdnica"
  // country: "PL"
  // name: "test"
  // postCode: "58-100"
  // street: "Księżnej Jadwigi Śląskiej 7/1"
  // vatId: "6546547655435654"
  //   [[Prototype]]: Object
  // createdAt: Tue Dec 14 2021 11:51:06 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // customColor: Color {model: 'rgb', color: Array(3), valpha: 1}
  // dateToPay: Thu Dec 16 2021 00:00:01 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // dateToPayAfterStart: Sat Dec 18 2021 00:00:01 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // departureTime: Thu Dec 23 2021 10:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // deposit: (...)
  // doorCode: "432fdsrw324"
  // doubleBed: "1"
  // finishDate: Thu Dec 23 2021 00:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // from_website: "0"
  // guest:
  //   apartmentNumber: "1"
  // city: "Świdnica"
  // country: "PL"
  // email: "wasylkiewicz.dawid@gmail.com"
  // houseNumber: "7"
  // idCard: "fsewrdsffdgdf"
  // name: "Dawid Wasylkiewicz"
  // passport: "3213esadda"
  // phone: "574593394"
  // postCode: "58-100"
  // street: "Księżnej Jadwigi Śląskiej 7/1"
  //   [[Prototype]]: Object
  // iCalendarId: null
  // id: "568910"
  // isDeleted: false
  // kids: 2
  // kidsAge: (4) ['2', '2', '0', '0']
  // localTax: (...)
  // localTaxIncluded: true
  // modifiedAt: Tue Dec 14 2021 11:51:06 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // notes: "fdsrewrew"
  // paymentOnPlace: (...)
  // paymentOnPlaceDate: Fri Dec 17 2021 00:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // paymentOnPlaceMethod: "Przelew bankowy"
  // prepaidType: "0"
  // price: (...)
  // pricePerDay: (...)
  // priceType: 0
  // provenance: "Email"
  // provision: (...)
  // provisionDate: Sat Dec 18 2021 00:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // provisionMethod: "Karta kredytowa"
  // registration: "1"
  // roomId: "46334"
  // roomMates: Array(2)
  // 0:
  // city: "Świdnica"
  // client_name: "Dawid2"
  // email: "wasylkiewicz.dawid@gmail.com"
  // id: "115115"
  // id_card: "fdgdrtrefd"
  // id_reservation: "568910"
  // local_number: "1"
  // nationality: "PL"
  // passport: "321321"
  // phone: "574593394"
  // post_code: "58-100"
  // street: "Księżnej Jadwigi Śląskiej 7/1"
  // street_number: "1"
  //   [[Prototype]]: Object
  // 1: {id: '115116', id_reservation: '568910', client_name: 'Dawid W', street: 'Księżnej Jadwigi Śląskiej 7/1', street_number: '1', …}
  // length: 2
  //   [[Prototype]]: Array(0)
  // service: "BB"
  // singleBed: "2"
  // stage: "1"
  // startDate: Tue Dec 21 2021 00:00:00 GMT+0100 (czas środkowoeuropejski standardowy) {}
  // _bookingPrice: null
  // _deposit: "321.00"
  // _localTax: "12.80"
  // _price: "118.80"
  // _pricePerDay: "59.40"
  // _provision: "41.92"
}
